import cn from 'classnames';
import Image from 'components/Shared/Image';
import Price from 'components/Shared/Price';
import { CustomerPageOrderLine } from 'models/CustomerPageOrderLine';
import { SupportProductModel } from 'models/SupportProductModel';
import { NO_ELEMENT } from 'utils';
import { QuantitySelector } from './QuantitySelector';
import styles from './styles.scss';

interface OwnProps {
  className?: string;
}

interface MergedProps extends OwnProps {
  line: CustomerPageOrderLine;
  displayMode: DisplayMode;
  isActive?: boolean;
  deliveryDate?: string;
  returnProductChanged?: (product: SupportProductModel) => void;
  hasTracking: boolean;
  isInstallationProduct: boolean | undefined;
}

interface DiscountProps {
  line: CustomerPageOrderLine;
}

const imageStyle = {
  width: 68,
  height: 68,
  display: 'inline-block'
};

const getText = (text: string, isActive: boolean) => {
  return isActive ? text : <del>{text}</del>;
};

export enum DisplayMode {
  Normal = 0,
  Returns = 1,
  Change = 2,
  Claims = 3,
  PriceMatch = 4
}

export const DiscountSpecification = ({ line }: DiscountProps) => {
  if (line.linePriceDiscountAmount === 0) {
    return NO_ELEMENT;
  }

  const discountPercent = (line: CustomerPageOrderLine) => {
    if (line.linePriceDiscountPercent === 0 || line.feeItem) {
      return NO_ELEMENT;
    }
    return <span className={styles.specification}>({line.linePriceDiscountPercent}%)</span>;
  };

  const discountAmount = (line: CustomerPageOrderLine) => {
    if (line.linePriceDiscountPercent > 0 || line.linePriceDiscountAmount === 0 || line.feeItem) {
      return NO_ELEMENT;
    }

    return (
      <span className={styles.specification}>
        <Price price={-line.linePriceDiscountAmount} appearance="normal" />
      </span>
    );
  };

  return (
    <div className={styles.discount}>
      {discountPercent(line)}
      {discountAmount(line)}
      <Price price={line.linePriceWithoutDiscount} appearance="lineThrough" />
    </div>
  );
};

export default function OrderLine({
  line,
  displayMode,
  isActive,
  deliveryDate,
  returnProductChanged,
  hasTracking,
  isInstallationProduct
}: MergedProps) {
  return (
    <div className={styles.orderLine}>
      <div className={styles.image}>
        {line.image ? (
          <div style={imageStyle}>
            <Image
              className={styles.imageStyle}
              {...{
                ...imageStyle,
                ...line.image,
                alt: line.image.name
              }}
            />
          </div>
        ) : (
          <div className={styles.imageStyle} style={imageStyle} />
        )}
      </div>
      <div className={styles.content}>
        <div className={styles.name}>
          <span className={styles.mainName}>{getText(line.name, line.isActive)}</span>
          {line.variantName && (
            <span className={styles.variantName}>{getText(line.variantName, line.isActive)}</span>
          )}
        </div>
        <div
          className={cn(styles.quantity, {
            [styles.quantityreturn]: displayMode !== DisplayMode.Normal
          })}
        >
          {getText(`${line.quantity} ${line.unit}`, line.isActive)}
        </div>
        {displayMode !== DisplayMode.Normal ? (
          <QuantitySelector
            line={line}
            isActive={isActive && !isInstallationProduct}
            deliveryDate={deliveryDate}
            onChange={returnProductChanged}
            displayMode={displayMode}
            hasTracking={hasTracking}
          />
        ) : (
          <div className={styles.price}>
            {!line.isRentalProduct && (
              <Price
                price={line.linePrice}
                appearance={line.isActive ? 'discount' : 'lineThrough'}
              />
            )}
            <DiscountSpecification line={line} />
          </div>
        )}
        {displayMode === DisplayMode.Normal && line.subOrderLines && (
          <div className={styles.subItems}>
            {line.subOrderLines.map(subline => (
              <div key={subline.lineNumber} className={styles.subItemLine}>
                <div className={styles.name}>{getText(`+ ${subline.name}`, subline.isActive)}</div>
                <div className={styles.quantity}>
                  {getText(`${subline.quantity} ${subline.unit}`, subline.isActive)}
                </div>
                <div className={styles.price}>
                  {!line.isRentalProduct && (
                    <Price
                      price={subline.linePrice}
                      appearance={subline.isActive ? 'discount' : 'lineThrough'}
                    />
                  )}
                  <DiscountSpecification line={subline} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
